'use client';

import { Quote, Star } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TESTIMONIALS_CONTENT } from '@/core/i18n/content/testimonials.content';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  StarRating                                                                */
/* -------------------------------------------------------------------------- */

function StarRating() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="h-3.5 w-3.5 fill-hms-gold text-hms-gold"
        />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  TestimonialCard                                                           */
/* -------------------------------------------------------------------------- */

interface TestimonialItem {
  quote: string;
  name: string;
  role: string;
  hotel: string;
  initials: string;
}

function TestimonialCard({ testimonial, index }: { testimonial: TestimonialItem; index: number }) {
  return (
    <div
      id={`hotels-testimonial-card-${index}`}
      className={cn(
        'flex flex-col rounded-xl border border-hms-border bg-hms-card p-6',
        'transition-colors hover:border-hms-gold/20',
      )}
    >
      <Quote className="mb-4 h-8 w-8 text-hms-gold/10" />

      <p className="flex-1 text-sm italic leading-relaxed text-hms-textSub">
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      <div className="my-5 h-px bg-hms-border" />

      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hms-gold/20">
          <span className="text-xs font-bold text-hms-gold">
            {testimonial.initials}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-hms-text">
            {testimonial.name}
          </p>
          <p className="truncate text-xs text-hms-textDim">
            {testimonial.role}, {testimonial.hotel}
          </p>
        </div>

        <StarRating />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Testimonials                                                              */
/* -------------------------------------------------------------------------- */

export interface TestimonialsProps {
  title?: string;
  subtitle?: string;
  quotes?: { id?: string; quote: string; author: string }[];
  sectionId?: string;
}

interface ResolvedTestimonial {
  id: string;
  quote: string;
  name: string;
  role: string;
  hotel: string;
  initials: string;
}

export function Testimonials(props: TestimonialsProps = {}) {
  const c = usePageContent(TESTIMONIALS_CONTENT);
  const sectionId = props.sectionId ?? 'hotels-testimonials-section';
  const title = props.title ?? c.title;
  const subtitle = props.subtitle ?? c.subtitle;
  const overrides = props.quotes;
  const quotes: ResolvedTestimonial[] = overrides
    ? overrides.map((q, idx) => {
        const id = q.id ?? `${sectionId}-${idx}`;
        const author = q.author ?? '';
        const initials = author
          .split(' ')
          .map((w: string) => w[0] ?? '')
          .slice(0, 2)
          .join('')
          .toUpperCase();
        return { id, quote: q.quote, name: author, role: '', hotel: '', initials };
      })
    : c.quotes.map((q, idx) => ({
        id: `${sectionId}-${idx}`,
        quote: q.quote,
        name: q.name,
        role: q.role,
        hotel: q.hotel,
        initials: q.initials,
      }));
  return (
    <section id={sectionId} className="bg-hms-surface/30 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 id={`${sectionId}-title`} className="text-3xl font-bold tracking-tight text-hms-text sm:text-4xl">
            {title}
          </h2>
          <p id={`${sectionId}-subtitle`} className="mt-3 text-sm text-hms-textSub">
            {subtitle}
          </p>
        </div>

        {/* Grid */}
        <div id={`${sectionId}-grid`} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {quotes.map((t, idx) => (
            <TestimonialCard key={`${t.initials}-${idx}`} testimonial={t} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
